import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFindMany, useGlobalAction, useUser } from '@gadgetinc/react';
import { api } from '../api';
import { hasAnyRole } from '../lib/roleUtils';

export default function AdminEmbedTokens() {
  const user = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('');
  const [assistantFilter, setAssistantFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form state for creating new embed token
  const [newTokenForm, setNewTokenForm] = useState({
    userEmail: '',
    hostDomain: '',
    assistantId: '',
    maxUses: '',
    expirationDays: '30'
  });

  // Check if user is authenticated and has admin role
  if (!user) {
    return (
      <div style={{ background: '#111111', minHeight: '100vh', color: 'white', padding: '2rem' }}>
        <div>Please sign in to access this page.</div>
      </div>
    );
  }

  const isAdmin = hasAnyRole(user.roles, ['admin', 'system-admin']);
  
  if (!isAdmin) {
    return (
      <div style={{ background: '#111111', minHeight: '100vh', color: 'white', padding: '2rem' }}>
        <div>You don't have permission to access this page.</div>
      </div>
    );
  }

  // Fetch embed tokens and assistants
  const [{ data: tokens, error: tokensError, fetching: tokensFetching }, refreshTokens] = useFindMany(api.embedToken, {
    select: {
      id: true,
      token: true,
      userEmail: true,
      hostDomain: true,
      createdAt: true,
      expiresAt: true,
      issuedAt: true,
      lastUsedAt: true,
      accessCount: true,
      isActive: true,
      maxUses: true,
      revokedAt: true,
      revokedReason: true,
      assistant: {
        id: true,
        name: true
      },
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      }
    },
    sort: { createdAt: 'Descending' }
  });

  const [{ data: assistants, error: assistantsError, fetching: assistantsFetching }] = useFindMany(api.assistant, {
    select: {
      id: true,
      name: true,
      description: true
    }
  });

  // Global actions for token management
  const [{ data: generateResult, error: generateError, fetching: generating }, generateToken] = useGlobalAction(api.generateEmbedToken);

  // Filter tokens based on search and filters
  const filteredTokens = useMemo(() => {
    if (!tokens) return [];
    
    return tokens.filter(token => {
      const matchesSearch = !searchTerm || 
        token.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        token.hostDomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
        token.assistant?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && token.isActive && new Date(token.expiresAt) > new Date()) ||
        (statusFilter === 'expired' && (!token.isActive || new Date(token.expiresAt) <= new Date())) ||
        (statusFilter === 'revoked' && token.revokedAt);
      
      const matchesDomain = !domainFilter || token.hostDomain.toLowerCase().includes(domainFilter.toLowerCase());
      
      const matchesAssistant = !assistantFilter || token.assistant?.id === assistantFilter;
      
      return matchesSearch && matchesStatus && matchesDomain && matchesAssistant;
    });
  }, [tokens, searchTerm, statusFilter, domainFilter, assistantFilter]);

  // Handle form submission for new token
  const handleCreateToken = async (e) => {
    e.preventDefault();
    
    if (!newTokenForm.userEmail || !newTokenForm.hostDomain || !newTokenForm.assistantId) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + parseInt(newTokenForm.expirationDays));
      
      await generateToken({
        userEmail: newTokenForm.userEmail,
        hostDomain: newTokenForm.hostDomain,
        assistantId: newTokenForm.assistantId,
        maxUses: newTokenForm.maxUses ? parseInt(newTokenForm.maxUses) : null,
        expiresAt: expirationDate.toISOString()
      });
      
      // Reset form and close
      setNewTokenForm({
        userEmail: '',
        hostDomain: '',
        assistantId: '',
        maxUses: '',
        expirationDays: '30'
      });
      setShowCreateForm(false);
      refreshTokens();
    } catch (error) {
      console.error('Error creating embed token:', error);
      alert('Failed to create embed token. Please try again.');
    }
  };

  // Mask token for display
  const maskToken = (token) => {
    if (!token || token.length < 8) return token;
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  };

  // Get token status
  const getTokenStatus = (token) => {
    if (token.revokedAt) return 'Revoked';
    if (!token.isActive) return 'Inactive';
    if (new Date(token.expiresAt) <= new Date()) return 'Expired';
    return 'Active';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#4CAF50';
      case 'Expired': return '#FF9800';
      case 'Revoked': return '#F44336';
      case 'Inactive': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const styles = {
    container: {
      background: '#111111',
      minHeight: '100vh',
      color: 'white',
      padding: '2rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    backButton: {
      background: '#333333',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      textDecoration: 'none',
      display: 'inline-block'
    },
    createButton: {
      background: '#007BFF',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      cursor: 'pointer'
    },
    filtersContainer: {
      background: '#1e1e1e',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '2rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.9rem',
      color: '#ccc'
    },
    input: {
      background: '#333333',
      color: 'white',
      border: '1px solid #555555',
      padding: '0.5rem',
      borderRadius: '4px'
    },
    select: {
      background: '#333333',
      color: 'white',
      border: '1px solid #555555',
      padding: '0.5rem',
      borderRadius: '4px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: '#1e1e1e',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    th: {
      background: '#333333',
      color: 'white',
      padding: '1rem',
      textAlign: 'left',
      fontWeight: 'bold'
    },
    td: {
      padding: '1rem',
      borderBottom: '1px solid #333333'
    },
    statusBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: 'bold'
    },
    actionButton: {
      background: 'transparent',
      color: '#007BFF',
      border: '1px solid #007BFF',
      padding: '0.25rem 0.5rem',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      marginRight: '0.5rem'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: '#1e1e1e',
      padding: '2rem',
      borderRadius: '8px',
      width: '90%',
      maxWidth: '500px',
      color: 'white'
    },
    formGroup: {
      marginBottom: '1rem'
    },
    formActions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
      marginTop: '2rem'
    },
    submitButton: {
      background: '#007BFF',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      cursor: 'pointer'
    },
    cancelButton: {
      background: '#6c757d',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <Link to="/admin" style={styles.backButton}>
            ← Back to Admin
          </Link>
          <h1 style={{ margin: '1rem 0 0 0' }}>Embed Tokens Management</h1>
        </div>
        <button 
          style={styles.createButton}
          onClick={() => setShowCreateForm(true)}
        >
          Create New Token
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Search</label>
          <input 
            type="text"
            placeholder="Search by email, domain, or assistant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.input}
          />
        </div>
        
        <div style={styles.filterGroup}>
          <label style={styles.label}>Status</label>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.label}>Domain</label>
          <input 
            type="text"
            placeholder="Filter by domain..."
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.label}>Assistant</label>
          <select 
            value={assistantFilter}
            onChange={(e) => setAssistantFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All Assistants</option>
            {assistants?.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tokens Table */}
      {tokensFetching ? (
        <div>Loading tokens...</div>
      ) : tokensError ? (
        <div style={{ color: '#F44336' }}>Error loading tokens: {tokensError.toString()}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Token</th>
              <th style={styles.th}>User Email</th>
              <th style={styles.th}>Assistant</th>
              <th style={styles.th}>Host Domain</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.th}>Usage</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTokens?.map(token => {
              const status = getTokenStatus(token);
              const statusColor = getStatusColor(status);
              
              return (
                <tr key={token.id}>
                  <td style={styles.td}>
                    <code style={{ background: '#333', padding: '0.25rem', borderRadius: '4px' }}>
                      {maskToken(token.token)}
                    </code>
                  </td>
                  <td style={styles.td}>{token.userEmail}</td>
                  <td style={styles.td}>{token.assistant?.name || 'N/A'}</td>
                  <td style={styles.td}>{token.hostDomain}</td>
                  <td style={styles.td}>
                    {new Date(token.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    {new Date(token.expiresAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    {token.accessCount} {token.maxUses ? `/ ${token.maxUses}` : ''}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: statusColor,
                      color: 'white'
                    }}>
                      {status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button 
                      style={styles.actionButton}
                      onClick={() => {
                        const details = `
Token: ${token.token}
User Email: ${token.userEmail}
Assistant: ${token.assistant?.name || 'N/A'}
Host Domain: ${token.hostDomain}
Created: ${new Date(token.createdAt).toLocaleString()}
Expires: ${new Date(token.expiresAt).toLocaleString()}
Last Used: ${token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : 'Never'}
Usage: ${token.accessCount} ${token.maxUses ? `/ ${token.maxUses}` : ''}
Status: ${status}
${token.revokedAt ? `Revoked: ${new Date(token.revokedAt).toLocaleString()}` : ''}
${token.revokedReason ? `Revoke Reason: ${token.revokedReason}` : ''}
                        `.trim();
                        alert(details);
                      }}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {filteredTokens?.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#ccc' }}>
          No embed tokens found matching your criteria.
        </div>
      )}

      {/* Create Token Modal */}
      {showCreateForm && (
        <div style={styles.modal} onClick={() => setShowCreateForm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2>Create New Embed Token</h2>
            <form onSubmit={handleCreateToken}>
              <div style={styles.formGroup}>
                <label style={styles.label}>User Email *</label>
                <input 
                  type="email"
                  value={newTokenForm.userEmail}
                  onChange={(e) => setNewTokenForm({...newTokenForm, userEmail: e.target.value})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Assistant *</label>
                <select 
                  value={newTokenForm.assistantId}
                  onChange={(e) => setNewTokenForm({...newTokenForm, assistantId: e.target.value})}
                  style={styles.select}
                  required
                >
                  <option value="">Select Assistant</option>
                  {assistants?.map(assistant => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Host Domain *</label>
                <input 
                  type="text"
                  placeholder="example.com"
                  value={newTokenForm.hostDomain}
                  onChange={(e) => setNewTokenForm({...newTokenForm, hostDomain: e.target.value})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Expiration (days)</label>
                <select 
                  value={newTokenForm.expirationDays}
                  onChange={(e) => setNewTokenForm({...newTokenForm, expirationDays: e.target.value})}
                  style={styles.select}
                >
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Max Uses (optional)</label>
                <input 
                  type="number"
                  placeholder="Leave empty for unlimited"
                  value={newTokenForm.maxUses}
                  onChange={(e) => setNewTokenForm({...newTokenForm, maxUses: e.target.value})}
                  style={styles.input}
                  min="1"
                />
              </div>

              {generateError && (
                <div style={{ color: '#F44336', marginBottom: '1rem' }}>
                  Error: {generateError.toString()}
                </div>
              )}

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  onClick={() => setShowCreateForm(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={styles.submitButton}
                  disabled={generating}
                >
                  {generating ? 'Creating...' : 'Create Token'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}